import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useLogActivity } from 'api'
import { USER_ROLES } from 'config/roles'
import { api } from 'lib/axios'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'lib'
import { getUserId } from 'lib/auth/tokenStorage'

const STATUS_CHANGE_WINDOW_DAYS = 90

const isOutsideStatusChangeWindow = dateEntered => {
  if (!dateEntered) return false
  const entered = moment.utc(dateEntered, 'YYYY-MM-DD HH:mm:ss')
  if (!entered.isValid()) return false
  return moment.utc().diff(entered, 'days') > STATUS_CHANGE_WINDOW_DAYS
}

export const useLockedCardStatusUpdater = () => {
  const purchase_cards = useSelector(state => state?.paymentCards?.paymentCards)
  const contactId = useSelector(state => state?.customer?.id)
  const customer = useSelector(state => state?.customer)
  const statusOverride = Number(customer?.status_override) || 0
  const userRole = useSelector(state =>
    state?.auth?.data?.current_user?.affiliate_role?.toLowerCase()
  )
  const statusChangeRef = useRef(false)
  const queryClient = useQueryClient()
  const { logApiActivity } = useLogActivity()

  const [isStatusChangeLoading, setIsStatusChangeLoading] = useState(false)

  const checkAndUpdateStatusForLockedCards = async (shouldInvalidate = true) => {
    if (!purchase_cards || purchase_cards.length === 0) return
    if (!contactId) return
    if (isOutsideStatusChangeWindow(customer?.date_entered)) {
      statusChangeRef.current = false
      return
    }

    const isAllowedRole =
      userRole === USER_ROLES.ADMIN ||
      userRole === USER_ROLES.AUTH_ACCESS ||
      userRole === USER_ROLES.MANAGER ||
      userRole === USER_ROLES.AGENT ||
      userRole === USER_ROLES.CUSTOMER_SERVICE ||
      userRole === USER_ROLES.BACK_OFFICE ||
      userRole === USER_ROLES.CROATIAN_TEAM

    if (!isAllowedRole) return

    const hasLockedCard = purchase_cards.some(card => {
      const cardFiles = card?.files || []
      const cardLength = cardFiles.length
      if (cardLength === 0) return false
      if (statusOverride === 1) return false

      const attrs = card?.attributes || {}
      const shouldLockCard = attrs.ocr_status !== '1'
      return shouldLockCard
    })

    const currentStatus = customer?.status_c

    if (hasLockedCard) {
      if (currentStatus === 'Auth Access' || currentStatus === 'auth_access') {
        statusChangeRef.current = true
        return
      }

      if (statusChangeRef.current && (currentStatus !== 'Auth Access' || currentStatus !== 'auth_access')) {
        statusChangeRef.current = false
      }

      if (statusChangeRef.current) {
        return
      }

      statusChangeRef.current = true
      const agentId = getUserId() || ''
      const statusChangePayload = {
        contact_id: contactId,
        agent_id: agentId,
        status_c: 'auth_access',
        status_override: 0,
        method: "auto",
        previous_status_c: currentStatus,
        is_locked_c: (currentStatus === 'Auth Access' || currentStatus === 'auth_access') ? 1 : 0
      }

      try {
        setIsStatusChangeLoading(true)
        const response = await logApiActivity({
          contactId: contactId,
          agentId,
          status_override: 0,
          apiEndpoint: '/Api/V8/custom/customer/set-status-change',
          actionType: 'status_change',
          fieldName: 'status_c',
          oldValue: currentStatus,
          newValue: 'auth_access',
          requestPayload: statusChangePayload,
          apiCall: async () =>
            api.post(
              '/Api/V8/custom/customer/set-status-change',
              statusChangePayload
            )
        })
        if (response?.data?.success === true && shouldInvalidate) {
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.CUSTOMERS_LIST]
          })
        }
        return response?.data?.success === true
      } catch (error) {
        console.error('Error calling status change API for locked cards:', error)
        statusChangeRef.current = false
      } finally {
        setIsStatusChangeLoading(false)
      }
    } else {
      statusChangeRef.current = false
    }
  }

  return Object.assign(checkAndUpdateStatusForLockedCards, {
    isStatusChangeLoading
  })
}

